import { useCallback, useEffect, useState } from "react";
import { Download, ImageIcon, Loader2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

/** The feature graphic, promo card and App Store icon, composed at the exact store sizes. */
export const StoreArtPanel = ({ app, onNotice }) => {
  const [art, setArt] = useState(null);
  const [sizes, setSizes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/mobile-apps/${app.id}/store-art`);
      setArt(data.art);
      setSizes(data.sizes);
    } catch (e) {
      setError(errMsg(e));
    }
  }, [app.id]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true); setError("");
    try {
      setArt((await api.post(`/admin/mobile-apps/${app.id}/store-art`)).data.art);
      onNotice?.("Store art generated.");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.get(`/admin/mobile-apps/${app.id}/store-art.zip`,
        { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${app.app_id}-store-art.zip`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="store-art-panel" className="rounded-xl border border-ink-600 bg-ink-800 p-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 font-display text-lg text-slate-100">
            <ImageIcon className="h-4 w-4 text-gold" /> Store art
          </p>
          <p className="mt-1 text-sm text-slate-400">
            {sizes.map((s) => `${s.label} (${s.pixels})`).join(" · ")
              || "Feature graphic, promo card and App Store icon"} — made in your black and gold,
            with this app's name and tagline on it.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="store-art-generate" disabled={busy} onClick={generate}
            className="bg-gold text-ink-900 hover:bg-gold-light">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <ImageIcon className="mr-2 h-4 w-4" />}
            {art?.length ? "Make again" : "Generate art"}
          </Button>
          {Boolean(art?.length) && (
            <Button data-testid="store-art-download" variant="outline" disabled={busy}
              onClick={download}>
              <Download className="mr-2 h-4 w-4" /> Download zip
            </Button>
          )}
        </div>
      </div>

      {error && <p data-testid="store-art-error" className="mt-3 text-sm text-red-400">{error}</p>}

      {Boolean(art?.length) && (
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {art.map((item) => (
            <a key={item.name} href={item.url} target="_blank" rel="noopener noreferrer"
              data-testid={`store-art-${item.name}`}
              className="group rounded-lg border border-ink-600 bg-ink-900/60 p-3 transition-colors hover:border-gold/40">
              <img src={item.url} alt={item.label}
                className="max-h-56 w-full rounded border border-ink-600 object-contain" />
              <p className="mt-2 text-sm text-slate-200">{item.label}</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                {item.pixels}
              </p>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};
